import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import isEmpty from 'lodash.isempty'
import startcase from 'lodash.startcase'
import { useEffect, useState } from 'react'

import { fetchPostSkedPreview, fetchGridPreview, fetchSetting } from 'api'
import { PostPreviewGrid } from 'Components'
import {
  handleStatusStateBg,
  handleStatusStateColor,
} from 'Components/UpcomingPostCard'
import useKeyPress from 'hooks/useKeyPress'
import { ArrowLeftIcon, ArrowRightIcon } from 'Icons'
import {
  normalizeSkedPreviewPost,
  normalizeGridPreview,
  isPostIsStory,
} from 'normalizer'
import type { Platform } from 'Types'
import { Button } from 'ui/button'
import type { PreviewVariant } from 'utils/postPreviewVariant'

import PlatformIcon from './Common/PlatformIcon'
import SkedPreviewFrame from './SkedPreviewFrame'

export interface SelectedTile {
  id: string
  date: string
  time: string
  platform: Platform
  status: string
}

export interface CalendarModalProps {
  reviewKey: string
  selectedTile: SelectedTile
  reviewPostClick: () => void
  navigationArrowClick: (
    direction: 'left' | 'right',
    currentId: string
  ) => () => void
  hideArrowLeft: boolean
  hideArrowRight: boolean
  isReadOnly: boolean
  previewVariant: PreviewVariant
}

const CalendarModal: React.FC<CalendarModalProps> = ({
  reviewKey,
  selectedTile,
  reviewPostClick,
  navigationArrowClick,
  hideArrowLeft,
  hideArrowRight,
  isReadOnly,
  previewVariant,
}) => {
  const [isLoadingSkedPreview, setIsLoadingSkedPreview] = useState(false)
  const [skedPreviewPost, setSkedPreviewPost] = useState<any>([])

  const [isLoadingGridData, setIsLoadingGridData] = useState<any>(undefined)
  const [gridData, setGridData] = useState<any>(undefined)

  useKeyPress('ArrowLeft', () => {
    const ArrowLeftElement = document.querySelector(
      '[data-testid="arrow-left"]'
    ) as HTMLElement | null

    ArrowLeftElement?.click()
  })
  useKeyPress('ArrowRight', () => {
    const ArrowRightElement = document.querySelector(
      '[data-testid="arrow-right"]'
    ) as HTMLElement | null

    ArrowRightElement?.click()
  })

  useEffect(() => {
    const fetchPostSkedPreviewAsync = async (
      reviewKey: string,
      postId: string
    ) => {
      try {
        setIsLoadingSkedPreview(true)
        const skedPreviewResponse = await fetchPostSkedPreview(
          reviewKey,
          postId
        )
        const setting = await fetchSetting(reviewKey)

        const normalizedSkedPreviewPost = normalizeSkedPreviewPost(
          skedPreviewResponse,
          setting.accounts
        )

        setSkedPreviewPost(normalizedSkedPreviewPost)
        setIsLoadingSkedPreview(false)

        if (
          skedPreviewResponse &&
          skedPreviewResponse.platform === 'IG' &&
          skedPreviewResponse.post_stage !== 'draft' &&
          !isPostIsStory(skedPreviewResponse)
        ) {
          setIsLoadingGridData(true)
          const gridPreviewResponse = await fetchGridPreview(reviewKey, postId)

          if (gridPreviewResponse) {
            const normalizedGridPreviewPost = normalizeGridPreview(
              gridPreviewResponse,
              postId
            )
            setGridData(normalizedGridPreviewPost)
          } else {
            setGridData(undefined)
          }
          setIsLoadingGridData(false)
        } else {
          setGridData(undefined)
        }
      } catch {
        // this appears to be how "empty state" is handled here
        setGridData(undefined)
      }
    }

    fetchPostSkedPreviewAsync(reviewKey, selectedTile.id)
  }, [selectedTile.id])

  const emptyGridData = isEmpty(gridData)

  return (
    <div
      data-testid="calendar-modal"
      className={cn(
        'mx-auto h-[92vh] overflow-y-auto',
        emptyGridData ? 'px-[60px]' : 'pl-[50px] pr-[60px]'
      )}
    >
      {/* Status badge — top-left corner, style-guide chip colours */}
      <div
        className="absolute left-0 top-0 z-[999] flex h-[50px] items-center rounded-bl-none rounded-br-[10px] rounded-tl-[10px] pr-4 text-base font-bold [&_svg]:mx-[13px] [&_svg]:my-4 [&_svg]:ml-4 [&_svg]:size-[23px]"
        style={{
          background: handleStatusStateBg(selectedTile.status, isReadOnly),
          color: isReadOnly
            ? '#ffffff'
            : handleStatusStateColor(selectedTile.status),
        }}
      >
        <PlatformIcon platform={selectedTile.platform} />
        {isReadOnly
          ? startcase(selectedTile.platform)
          : startcase(selectedTile.status)}
        {selectedTile.status !== 'requestChange' && (
          <>
            <span className="px-1">•</span>
            {selectedTile.time}, {selectedTile.date}
          </>
        )}
      </div>

      {isLoadingSkedPreview || isLoadingGridData || isEmpty(skedPreviewPost) ? (
        <div className="absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 text-base">
          Loading...
        </div>
      ) : (
        <>
          <div
            className={cn(
              'mt-16 flex pb-[50px]',
              emptyGridData ? 'justify-center' : 'justify-evenly'
            )}
          >
            <div>
              <SkedPreviewFrame
                post={skedPreviewPost}
                previewVariant={previewVariant}
                legacyClassName="mx-auto w-[360px] overflow-auto rounded-[10px] bg-white shadow-[3px_0_10px_rgba(0,0,0,0.15)]"
              />
            </div>
            {!emptyGridData && (
              <div className="relative h-[510px] [&_.container]:pl-[54px] [&_.container]:pt-16 [&_.first-grid-item]:border-none [&_.grid-container]:max-h-[386px]">
                <PostPreviewGrid
                  {...gridData}
                  isReadOnly={isReadOnly}
                  scrollableGrid
                />
              </div>
            )}
          </div>

          {!isReadOnly && (
            <div className="absolute inset-x-0 bottom-0 z-[3] mx-auto">
              <Button
                variant="green-primary"
                onClick={reviewPostClick}
                className="mx-auto my-[30px] flex w-[240px]"
              >
                Review Post
              </Button>
            </div>
          )}

          {!hideArrowLeft && (
            <div
              className="absolute left-[10px] top-1/2 w-10 [&_svg]:cursor-pointer"
              data-testid="arrow-left"
              onClick={navigationArrowClick('left', selectedTile.id)}
            >
              <ArrowLeftIcon />
            </div>
          )}
          {!hideArrowRight && (
            <div
              className="absolute right-[10px] top-1/2 w-10 [&_svg]:cursor-pointer"
              data-testid="arrow-right"
              onClick={navigationArrowClick('right', selectedTile.id)}
            >
              <ArrowRightIcon />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CalendarModal
